import { ChangeEvent, ReactElement, SyntheticEvent, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { usePdpTypesQuery } from 'common/hooks/api/queries/use-pdp-query';
import { Styles } from 'common/types/styles';
import { userService } from 'common/services/user.service';
import { RetailerType, SyndigoRetailers } from 'common/constants/entities';
import { LightTooltip } from 'common/ui/containers/light-tooltip';
import InfoIcon from '@mui/icons-material/Info';
import { ContainerWithLoading } from 'common/ui/containers/container-with-loading';
import { useModalsContext } from 'modules/common/product-details-page/components/modals-context';
import { PDPGenerationStatus } from 'modules/common/product-details-page/constants';
import { ProductAITabValue } from './constants';
import { TemplateType } from '../attribute-ai-modal/template-type';

const styles: Styles = {
  container: {
    overflowY: 'auto',
    maxHeight: '440px',
    p: 2,
    boxSizing: 'border-box',
    mb: 2,
  },
  checkboxContainer: { mb: 2, ml: 1 },
  checkboxes: { display: 'flex', flexDirection: 'column', ml: 1, mb: 2 },
  labelTip: { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 },
  selectContainer: { mb: 2, display: 'flex', flexDirection: 'column', gap: 1 },
  selectLabel: { fontSize: '12px', color: 'text.secondary' },
};

interface Props {
  selectedTab: ProductAITabValue;
  pid: string;
}

export function PdpTemplateContent({ selectedTab, pid }: Props): ReactElement {
  const [expandedIndex, setExpandedIndex] = useState<number | false>(false);

  const handleAccordionChange = (index: number) => (_: SyntheticEvent, isExpanded: boolean) => {
    setExpandedIndex(isExpanded ? index : false);
  };

  const {
    pdpAttributesWithIssuesOnly,
    pdpGeneration,
    pdpSeoOptimization,
    pdpGeoOptimization,
    enhanceWithSeasonality,
    seasonalitySourceCopy,
    pdpLoadingStatus,
    setPdpGeneration,
    setPdpSeoOptimization,
    setPdpGeoOptimization,
    geoRefetchReviews,
    setEnhanceWithSeasonality,
    setSeasonalitySourceCopy,
    setGeoRefetchReviews,
    setPdpAttributesWithIssuesOnly,
  } = useModalsContext();

  const { data: types } = usePdpTypesQuery();

  const isSEOOptimizationAvailable =
    userService.ensureRetailer(RetailerType.Target) ||
    userService.ensureRetailer(RetailerType.Instacart) ||
    userService.ensureRetailer(RetailerType.ChickSaddle) ||
    userService.ensureRetailers(SyndigoRetailers) ||
    userService.ensureRetailer(RetailerType.Autozone) ||
    userService.ensureRetailer(RetailerType.Amazon);

  const handlePdpGenerationChange = (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setPdpGeneration(checked);
  };

  const handlePdpSeoOptimization = (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setPdpSeoOptimization(checked);
  };

  const handlePdpGeoOptimization = (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setPdpGeoOptimization(checked);
    if (!checked) setGeoRefetchReviews(false);
  };

  const handleGeoRefetchReviews = (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setGeoRefetchReviews(checked);
  };

  const handleEnhanceWithSeasonality = (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setEnhanceWithSeasonality(checked);
  };

  const handleSeasonalitySourceCopyChange = (value: string) => {
    setSeasonalitySourceCopy(value);
  };

  const handleChangeAttributesWithIssuesOnly = (_: ChangeEvent<HTMLInputElement>, value: boolean) => {
    setPdpAttributesWithIssuesOnly(value);
  };

  const getOptimizationLoading = (): string => {
    switch (pdpLoadingStatus) {
      case PDPGenerationStatus.PDP_OPTIMIZATION_RUNNING:
        return 'PDP Optimization is running...';
      case PDPGenerationStatus.PDP_SEO_OPTIMIZATION_RUNNING:
        return 'PDP SEO Optimization is running...';
      case PDPGenerationStatus.PRODUCT_TITLE_OPTIMIZATION_RUNNING:
        return 'Product Title Optimization is running...';
      case PDPGenerationStatus.PRODUCT_DESCRIPTION_OPTIMIZATION_RUNNING:
        return 'Product Description Optimization is running...';
      case PDPGenerationStatus.PRODUCT_FEATURES_OPTIMIZATION_RUNNING:
        return 'Product Features Optimization is running...';
      default:
        return 'PDP Optimization is running...';
    }
  };

  return (
    <Box>
      <ContainerWithLoading
        loadingText={getOptimizationLoading()}
        loading={pdpLoadingStatus !== PDPGenerationStatus.IDLE}
        sx={styles.container}
      >
        {types?.map((type, i) => (
          <Accordion
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            expanded={expandedIndex === i}
            onChange={handleAccordionChange(i)}
            sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{type.displayName}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TemplateType selectedTab={selectedTab as number} pid={pid} type={type} hideTitle />
            </AccordionDetails>
          </Accordion>
        ))}
      </ContainerWithLoading>
      {userService.ensureRetailer(RetailerType.Walmart) && (
        <FormControlLabel
          sx={styles.checkboxContainer}
          control={<Checkbox checked={pdpAttributesWithIssuesOnly} onChange={handleChangeAttributesWithIssuesOnly} />}
          label={<Typography>Only PDP with Issues</Typography>}
        />
      )}
      {isSEOOptimizationAvailable && (
        <Box sx={styles.checkboxes}>
          {userService.ensureRetailers([
            RetailerType.Target,
            RetailerType.Amazon,
            RetailerType.Autozone,
            RetailerType.ChickSaddle,
          ]) && (
            <FormControlLabel
              control={<Checkbox checked={pdpGeneration} onChange={handlePdpGenerationChange} />}
              // eslint-disable-next-line react/no-unescaped-entities
              label={
                <Box sx={styles.labelTip}>
                  <Typography sx={styles.label}>PDP Optimization</Typography>
                  <LightTooltip title="Updates copy according to best practices. Includes grammar, length, and structure changes to the original copy.">
                    <IconButton size="small">
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </LightTooltip>
                </Box>
              }
            />
          )}

          <FormControlLabel
            control={<Checkbox checked={pdpSeoOptimization} onChange={handlePdpSeoOptimization} />}
            // eslint-disable-next-line react/no-unescaped-entities
            label={
              <Box sx={styles.labelTip}>
                <Typography sx={styles.label}>PDP SEO Optimization</Typography>
                <LightTooltip title="Inserts keywords surgically based on search data into either original copy or PDP optimized copy.">
                  <IconButton size="small">
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </LightTooltip>
              </Box>
            }
          />

          {userService.ensureRetailer(RetailerType.Target) && (
            <>
              <FormControlLabel
                control={<Checkbox checked={pdpGeoOptimization} onChange={handlePdpGeoOptimization} />}
                // eslint-disable-next-line react/no-unescaped-entities
                label={
                  <Box sx={styles.labelTip}>
                    <Typography sx={styles.label}>PDP GEO Optimization</Typography>
                  </Box>
                }
              />

              {pdpGeoOptimization && (
                <FormControlLabel
                  sx={{ ml: 3 }}
                  control={<Checkbox checked={geoRefetchReviews} onChange={handleGeoRefetchReviews} />}
                  label={<Typography sx={styles.label}>Refetch Reviews and Rerun Analysis</Typography>}
                />
              )}

              <>
                <FormControlLabel
                  control={<Checkbox checked={enhanceWithSeasonality} onChange={handleEnhanceWithSeasonality} />}
                  label={
                    <Box sx={styles.labelTip}>
                      <Typography sx={styles.label}>Enhance with Seasonality</Typography>
                    </Box>
                  }
                />
                {enhanceWithSeasonality && (
                  <Box sx={styles.selectContainer}>
                    <Typography sx={styles.selectLabel}>Source Copy</Typography>
                    <Select
                      value={seasonalitySourceCopy}
                      size="small"
                      onChange={e => handleSeasonalitySourceCopyChange(e.target.value)}
                      sx={{ minWidth: 200 }}
                    >
                      <MenuItem value="optimized_pdp">Optimized PDP</MenuItem>
                      <MenuItem value="seo_optimized_pdp">SEO Optimized PDP</MenuItem>
                      <MenuItem value="geo_optimized_pdp">GEO Optimized PDP</MenuItem>
                    </Select>
                  </Box>
                )}
              </>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}
